import type { DefaultValues, FieldValues, Mode } from "react-hook-form"
import type { z } from "zod"
import { useCallback, useEffect, useRef, useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"

interface ControlledPromise<T> {
	promise: Promise<T>
	resolve: (value: T) => void
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	reject: (reason?: any) => void
}
function createControlledPromise<T>(): ControlledPromise<T> {
	let resolve: (value: T) => void
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	let reject: (reason?: any) => void
	const promise = new Promise<T>((res, rej) => {
		resolve = res
		reject = rej
	})
	return { promise, resolve: resolve!, reject: reject! }
}

export interface OnSaveSuccessMeta<T> {
	old: T
}

type OnSaveFunc<T> = (data: T, optimisticData: T) => Promise<T>

type Transcation<T> = [OnSaveFunc<T>, T, ControlledPromise<T>]
interface QueuedFormSaveProps<T> {
	schema: z.ZodType<T>
	defaultValues: DefaultValues<T>
	saveFunc: OnSaveFunc<T>
	onSuccess?: (data: T, meta: OnSaveSuccessMeta<T>) => void
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onFail?: (data: T, error: any) => void
	/** Field path to message for every value that blocked the save. */
	onInvalid?: (errors: Record<string, string>) => void
	autoSave?: boolean
	autoSaveDelay?: number
	preventResetAfterSyncDelay?: number // Prevent reset for n ms after sync - avoids short flicker on save (created replicache delayed update)
	mode?: Mode
}

export function useSavableForm<T extends FieldValues>({
	schema,
	defaultValues,
	saveFunc,
	onSuccess,
	onFail,
	onInvalid,
	autoSave,
	autoSaveDelay = 1000,
	preventResetAfterSyncDelay = 5000,
	mode,
}: QueuedFormSaveProps<T>) {
	const form = useForm<T>({
		resolver: zodResolver(schema),
		defaultValues,
		mode,
	})

	const [runs, setRuns] = useState(0)
	const isSaving = useRef(false)
	const isDirty = useRef(false)
	const isDataSynced = useRef(true)
	const lastSyncedData = useRef<T>(form.getValues())
	const dataSyncedAt = useRef<number>(new Date().getTime())
	const lastChange = useRef<number>(0)
	const queue = useRef([] as Transcation<T>[])
	const formValues = form.watch()
	// react-hook-form's `formState` is a per-render snapshot, so `save` can't read
	// it after a same-tick `setValue`. Keep our own baseline instead, refreshed
	// whenever the form is pristine so an external `form.reset` moves it too.
	const isFormDirty = form.formState.isDirty
	const pristineValues = useRef(JSON.stringify(form.getValues()))
	if (!isFormDirty) {
		pristineValues.current = JSON.stringify(form.getValues())
	}
	const hasUnsavedChanges = () =>
		JSON.stringify(form.getValues()) !== pristineValues.current

	useEffect(() => {
		if (isFormDirty) {
			isDirty.current = true
			lastChange.current = Date.now()
		}
	}, [formValues, isFormDirty])

	const runNext = async () => {
		const nextQueue = queue.current.splice(0, queue.current.length)
		if (nextQueue.length === 0) {
			return
		}
		const [next] = nextQueue
		if (!next) return

		const [saveFunc, saveData] = next
		isSaving.current = true
		try {
			const old = lastSyncedData.current
			const result = await saveFunc(saveData, form.getValues() ?? saveData)
			onSuccess?.(result, { old })
			nextQueue
				.reverse()
				.forEach(([, optimisticData, promise]) =>
					promise.resolve(optimisticData),
				)
		} catch (error) {
			onFail?.(saveData, error)
			nextQueue.reverse().forEach(([, , promise]) => promise.reject(error))
		}
		isSaving.current = false
		setRuns((prev) => prev + 1)

		lastSyncedData.current = saveData
		dataSyncedAt.current = Date.now()
		if (queue.current.length > 0) {
			void runNext()
		} else {
			isDataSynced.current = true
		}
	}

	// A pristine save is never a no-op downstream: saveFunc pushes the form's
	// values to Linear, so resending an untouched copy overwrites a newer edit.
	const save = useCallback(
		async (options?: { force?: boolean }) => {
			if (!options?.force && !isDirty.current && !hasUnsavedChanges()) {
				return form.getValues()
			}
			const valid = await form.trigger()
			if (!valid) {
				// formState is a render snapshot, so read the issues off the schema.
				const parsed = schema.safeParse(form.getValues())
				onInvalid?.(
					parsed.success
						? {}
						: Object.fromEntries(
								parsed.error.issues.map((issue) => [
									issue.path.join("."),
									issue.message,
								]),
							),
				)
				return form.getValues()
			}
			const data = form.getValues()
			form.reset(data)
			await new Promise((resolve) => setTimeout(resolve, 0)) // Ensure the form state is reset before proceeding
			isDirty.current = false
			isDataSynced.current = false
			const promise = createControlledPromise<T>()
			queue.current.unshift([saveFunc, data, promise])
			if (!isSaving.current) {
				void runNext()
			}
			return promise.promise
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[saveFunc, onInvalid, schema],
	)

	const reset = useCallback(
		(data: T) => {
			if (isSaving.current) {
				console.warn("Prevented form reset due to saving")
				return
			}

			const lastSyncDelta = Date.now() - dataSyncedAt.current
			if (lastSyncDelta < preventResetAfterSyncDelay) {
				console.warn("Prevented form reset due to sync delay")
				return
			}
			form.reset(data)
			lastSyncedData.current = data
			isDirty.current = false
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isDirty, form],
	)

	// The interval is armed once, so it must reach the current `save` through a
	// ref. Closing over the mount-time one froze whatever state `saveFunc` reads.
	const saveRef = useRef(save)
	useEffect(() => {
		saveRef.current = save
	}, [save])

	useEffect(() => {
		if (!autoSave) return
		const interval = setInterval(() => {
			const lastChangeDelta = Date.now() - lastChange.current
			if (isDirty.current && lastChangeDelta > autoSaveDelay) {
				void saveRef.current()
			}
		}, autoSaveDelay / 2)
		return () => clearInterval(interval)
	}, [autoSave, autoSaveDelay])

	// The interval alone loses up to autoSaveDelay of typing, and mobile browsers
	// discard a backgrounded tab without firing blur.
	useEffect(() => {
		if (!autoSave) return
		const flush = () => {
			if (!isDirty.current) return
			void saveRef.current()
		}
		const onVisibilityChange = () => {
			if (document.visibilityState === "hidden") flush()
		}
		document.addEventListener("visibilitychange", onVisibilityChange)
		window.addEventListener("pagehide", flush)
		return () => {
			document.removeEventListener("visibilitychange", onVisibilityChange)
			window.removeEventListener("pagehide", flush)
			flush()
		}
	}, [autoSave])

	return {
		runs,
		form,
		save,
		reset,
		isSaving: isSaving.current,
		isDirty: isDirty.current,
		isDataSynced: isDataSynced.current,
		dataSyncedAt: dataSyncedAt.current,
	}
}
